import {
  Button,
  Checkbox,
  Group,
  Modal,
  Radio,
  Text,
  Title,
} from "@mantine/core";
import { useState } from "react";
import { useAppContext } from "../AppContext";

export default function SecurityQuestionsModal() {
  const {
    securityQuestionModalOpened,
    closeSecurityQuestionModal,
    closeAllModals,
    openMapConfigDrawer,
    setSelectedQuestion,
    selectedQuestion,
    openWelcomeModal,
    selectedLocations,
    selectedLocationIndex,
    setSelectedLocations,
  }: any = useAppContext();
  const securityQuestions: any = [
    {
      category: "childhood_and_early_life",
      category_label: "Childhood and Early Life",
      questions: [
        "Where did you live when you were born or before you turned 10?",
        "What was the name of the street or neighborhood where you grew up?",
        "Where did you go to elementary or primary school?",
        "What was the name of the park or playground where you spent time as a child?",
        "Where did you work during your first job or internship?",
      ],
    },
    {
      category: "travel_and_adventure",
      category_label: "Travel and Adventure",
      questions: [
        "Where did you travel for your first international vacation?",
        "What is the name of a place where you felt completely at peace during a trip?",
        "Where did you go on your first solo adventure or a memorable family trip?",
        "Where did you visit that drastically changed your perspective or life?",
        "Where was your favorite place to travel to as a child?",
      ],
    },
    {
      category: "educational_milestones",
      category_label: "Educational Milestones",
      questions: [
        "Where did you attend university or college? What city was it in?",
        "Where did your graduation ceremony take place?",
        "What city or town was the location where you earned your first certification or degree?",
        "Where did you meet your first college roommate?",
        "Where did you study abroad, if you did?",
      ],
    },
    {
      category: "career_and_professional_milestones",
      category_label: "Career and Professional Milestones",
      questions: [
        "Where was your first official job or internship?",
        "Where were you when you landed your dream job or received a promotion?",
        "Where did you launch your first business or start a professional venture?",
        "Where did you attend your first major business conference?",
        "Where did you first meet your mentor or someone who influenced your career?",
      ],
    },
    {
      category: "significant_personal_achievements",
      category_label: "Significant Personal Achievements",
      questions: [
        "Where were you when you achieved a major personal goal (e.g., ran a marathon, bought your first car)?",
        "Where did you overcome a personal challenge (e.g., recovery after illness or overcoming a fear)?",
        "Where did you perform or showcase a talent for the first time (e.g., music, theater)?",
        "Where did you take part in an event that made you feel proud of yourself?",
        "Where did you first volunteer or give back to the community?",
      ],
    },
    {
      category: "friendships_and_social_connections",
      category_label: "Friendships and Social Connections",
      questions: [
        "Where did you first meet your closest friend?",
        "Where did you meet some of your first adult friends (e.g., first workplace)?",
        "Where did you and your friends often hang out or create memories together?",
        "Where did you and your friends go on your first trip together?",
        "Where did you celebrate a milestone with your friends (e.g., graduation, promotion)?",
      ],
    },
    {
      category: "hobbies_and_personal_interests",
      category_label: "Hobbies and Personal Interests",
      questions: [
        "Where did you first start pursuing a significant hobby (e.g., a gym, art studio, park)?",
        "Where do you go to relax and unwind, away from everyday life (e.g., a lake, library, café)?",
        "Where did you work on or discover your passion (e.g., a writing spot, workshop)?",
        "Where did you first learn about a hobby or interest that became important to you?",
        "Where do you go to engage with a community around a shared interest?",
      ],
    },
    {
      category: "life_changing_experiences",
      category_label: "Life-changing Experiences",
      questions: [
        "Where did you get your first car? What city or dealership?",
        "Where were you when you experienced a major life-changing event (e.g., getting into a serious accident, meeting a mentor)?",
        "Where were you when you made a critical life decision (e.g., moving to a new city, starting a business)?",
        "Where did you first experience a life-changing challenge that shaped your future?",
        "Where did you go to process a major change in your life (e.g., divorce, job loss)?",
      ],
    },
    {
      category: "celebrations_and_important_events",
      category_label: "Celebrations and Important Events",
      questions: [
        "Where did you celebrate your first major personal celebration (e.g., 18th birthday, graduation, or wedding anniversary)?",
        "Where did you attend an event that had a major impact on you (e.g., concert, festival, protest)?",
        "Where did you attend an unforgettable party or social event?",
        "Where did you and your family celebrate your first holiday together?",
        "Where did you host your first big social gathering (e.g., wedding, family reunion)?",
      ],
    },
    {
      category: "health_and_wellness",
      category_label: "Health and Wellness",
      questions: [
        "Where was the doctor or health facility you visited for an important medical treatment or life event (e.g., pregnancy, surgery)?",
        "Where did you begin your fitness or wellness journey (e.g., first gym, yoga studio)?",
        "Where did you go for therapy or counseling that helped you overcome something significant?",
        "Where did you first receive a health diagnosis that led to a major change in your life?",
        "Where did you go to find a support group for health or wellness?",
      ],
    },
    {
      category: "community_and_giving_back",
      category_label: "Community and Giving Back",
      questions: [
        "Where did you first volunteer or participate in community service?",
        "What was the location of a religious or spiritual experience (e.g., first church visit, temple, meditation retreat)?",
        "Where did you attend or participate in a charity event that meant a lot to you?",
        "Where did you go to get involved in a community-based project or cause?",
        "Where did you first feel connected to a larger social cause or movement?",
      ],
    },
    {
      category: "pets_and_animal_companions",
      category_label: "Pets and Animal Companions",
      questions: [
        "Where did you get your first pet?",
        "Where did your pet love to go, such as a park or trail?",
        "Where did you have a life-changing or memorable experience with an animal (e.g., animal rescue, significant encounter with wildlife)?",
        "Where did you first adopt an animal from (e.g., shelter, breeder)?",
        "Where did you take your pet on your first big adventure together?",
        "Where did you visit that had a special place for pets or animals, like an animal sanctuary or zoo?",
      ],
    },
  ];
  const [selectedCategory, setSelectedCategory]: any = useState(null);

  const handleCategorySelect = (category: any) => {
    setSelectedCategory(category);
  };
  const handleQuestionSelect = (question: any) => {
    setSelectedQuestion(question);
  };
  return (
    <>
      <Modal
        opened={securityQuestionModalOpened}
        onClose={() => closeSecurityQuestionModal()}
        title={<Title order={2}>Security Question Suggestions</Title>}
        size="lg"
        withCloseButton={false}
        closeOnClickOutside={false}
        closeOnEscape={false}
        yOffset={60}
      >
        {/* Show Categories */}
        {!selectedCategory ? (
          <div>
            <Text>
              Select a category that will trigger a deeply personal and private
              memory.
            </Text>
            <Group dir="column" mt="md">
              {securityQuestions.map((category: any) => (
                <Button
                  key={category.category}
                  onClick={() => handleCategorySelect(category)}
                  variant="light"
                  fullWidth
                >
                  {category.category_label}
                </Button>
              ))}
            </Group>
          </div>
        ) : (
          /* Show Questions for Selected Category */
          <div>
            <Text w={500} size="lg" mb="md">
              Questions for {selectedCategory.category_label}
            </Text>
            {selectedCategory.questions.map((question: any, index: any) => (
              <Radio
                key={index}
                label={question}
                value={question}
                checked={selectedQuestion === question}
                onChange={() => {
                  handleQuestionSelect(question);
                  closeAllModals();
                  openWelcomeModal();
                  selectedLocations[selectedLocationIndex].question = question;
                  setSelectedLocations([...selectedLocations]);
                }}
                mt="sm"
              />
            ))}
            <Button
              variant="light"
              fullWidth
              mt="md"
              onClick={() => {
                setSelectedCategory(null);
              }}
            >
              Back to Categories
            </Button>
          </div>
        )}
      </Modal>
    </>
  );
}
